<script setup lang="ts">
import { computed } from 'vue';

import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import {
  useBrowserStore,
  useRouterStore,
} from '@apps/main/new-desktop/core/stores';
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';

import { AppRawButton, AppStars, AppTag, AppRouterLink } from '@libs/ui';

import type { AlternativeHotel } from '../../../models';

interface AlternativeAccommodationCardContentProps {
  hotel: AlternativeHotel;
  ingredientIndex: number;
}

const props = defineProps<AlternativeAccommodationCardContentProps>();

const browserStore = useBrowserStore();
const routerStore = useRouterStore();

interface AlternativeAccommodationCardContentEmits {
  (eventName: 'show-details'): void;
  (eventName: 'show-rooms'): void;
}

const emit = defineEmits<AlternativeAccommodationCardContentEmits>();

const hasMultipleRooms = computed<boolean>(
  () => props.hotel.roomsInfo.length > 1
);
</script>

<template>
  <div
    class="alternative-accommodation-card-content"
    :class="{
      mobile: browserStore.isSmallerThan600px,
    }"
  >
    <div
      role="button"
      tabindex="0"
      class="info-container"
      @click.stop="emit('show-details')"
      @keydown.enter.stop="emit('show-details')"
    >
      <AppRouterLink
        :link="
          routerStore.getHotelDetailsUrlInTripOverview(
            { ingredientIndex: props.ingredientIndex },
            props.hotel.hotelCode
          )
        "
        font="font-body-m-bold"
        color="color-neutral-70"
        class="name"
      >
        {{ props.hotel.hotelName }}
      </AppRouterLink>

      <span class="polygon-name">
        {{ props.hotel.polygonName }}
      </span>

      <div class="info-wrapper">
        <AppStars size="18" :amount="props.hotel.stars" />
        <CustomersRatingTag
          v-if="props.hotel.guestRating"
          :guest-rating="props.hotel.guestRating"
          font-size="s"
        />
        <AppTag v-if="props.hotel.mealName" type="grey" size="s">
          <template #title>
            <span class="tag-text">
              {{ props.hotel.mealName }}
            </span>
          </template>
        </AppTag>
      </div>
      <HotelAmenities :amenities="props.hotel.amenities" />
    </div>

    <AppRawButton class="rooms-container" @click.stop="emit('show-rooms')">
      <div
        v-for="element in props.hotel.roomsInfo"
        :key="element.label + element.description"
        :class="['description', { 'single-room': !hasMultipleRooms }]"
      >
        <span v-if="hasMultipleRooms" class="label">
          {{ element.label }}
        </span>
        {{ element.description }}
      </div>
    </AppRawButton>
  </div>
</template>

<style scoped lang="scss">
.alternative-accommodation-card-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  justify-content: space-between;
  height: 100%;

  &.mobile {
    padding-bottom: var(--spacing-12);
    .info-container {
      gap: 0;
    }

    .info-wrapper {
      margin: var(--spacing-4) 0 var(--spacing-8) 0;
    }
  }
  .info-container {
    display: grid;
    grid-template-rows: min-content min-content min-content auto;
    gap: var(--spacing-8);
    height: 100%;

    .hotel-name {
      color: var(--color-soft-black);

      display: -webkit-box;
      max-width: var(--card-width);
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0;
      text-decoration: none;

      @include font-label-m-bold;
      @include on-hover {
        cursor: pointer;
        text-decoration: underline;
      }
    }

    .polygon-name {
      @include font-label-s-light;
      color: var(--color-neutral-70);
    }

    .info-wrapper {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-8);

      .tag-text {
        @include font-label-s-bold;
      }
    }
  }

  .rooms-container {
    padding: var(--spacing-12);
    border: 1px solid var(--color-scrollbar);
    border-radius: var(--border-radius-8);

    .description {
      @include font-label-s-light;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      width: 0;
      min-width: 100%;

      .label {
        @include font-label-s-bold;
        white-space: nowrap;
      }

      &.single-room {
        white-space: normal;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        line-clamp: 2;
        -webkit-line-clamp: 2;
      }
    }
  }
}
</style>
